import React, { useState } from 'react';
import clsx from 'clsx';

import { CHAT_ATTACHMENT_COPY, SENT_ATTACHMENT_VISIBLE_LIMIT } from '../../../../../../../../store/dashboard/askAica/constants';
import {
    SENT_ATTACHMENT_RENDER,
    sentAttachmentRender,
    toSentChip,
} from '../../../../../../../../store/dashboard/askAica/attachmentChipState';
import { ClaimedAttachment } from '../../../../../../../../store/dashboard/askAica/types';
import { KEYBOARD_EVENTS } from '../../../../../../../../utils/constants/commonConstants';
import Button from '../../../../../../../../common/_custom/Button/Button';
import LoadingSpinner from '../../../../../../../../common/_custom/LoadingSpinner/LoadingSpinner';
import { AskAicaFileRef } from '../../hooks/useAskAicaFileActions';
import AttachmentChips from '../AttachmentChips/AttachmentChips';
import FileActionsMenu from '../FileAttachmentCard/FileActionsMenu';
import { getFileIcon } from '../FileAttachmentCard/FileAttachmentCard.constants';
import styles from './SentAttachmentCards.module.scss';

/** Equivalent to Athena's build_download_url after decode (percent-encoding differs for `/` and `!'()*`); built client-side so an upload never arrives under metadata.attachments / file_url (the Library feed). */
export const buildChatDownloadUrl = (docId: string, fileName: string) =>
    `/athena/chat/download/${docId}${fileName ? `?filename=${encodeURIComponent(fileName)}` : ''}`;

interface SentAttachmentCardsProps {
    attachments: ClaimedAttachment[];
    onDownload: (fileUrl: string, fileName: string) => void;
    /** Opens the file in the in-app viewer — the same handler the generated-file card uses. */
    onOpenFile?: (file: AskAicaFileRef) => void;
    /** URL currently being opened; shows the spinner on that card. */
    openingFileUrl?: string | null;
}

/** Compact cards for files a user sent; a pending entry has no row yet, and the rest stay as filing-state chips. */
const SentAttachmentCards: React.FC<SentAttachmentCardsProps> = ({ attachments, onDownload, onOpenFile, openingFileUrl }) => {
    const [expanded, setExpanded] = useState(false);
    const cardEntries = attachments.filter((attachment) => sentAttachmentRender(attachment) !== SENT_ATTACHMENT_RENDER.CHIP);
    const chipEntries = attachments.filter((attachment) => sentAttachmentRender(attachment) === SENT_ATTACHMENT_RENDER.CHIP);
    const visibleCards = expanded ? cardEntries : cardEntries.slice(0, SENT_ATTACHMENT_VISIBLE_LIMIT);
    const hiddenCount = cardEntries.length - visibleCards.length;

    return (
        <>
            {cardEntries.length > 0 && (
                <div className={styles.CardGrid} data-testid="sent-attachment-cards">
                    {visibleCards.map((attachment) => {
                        const fileUrl = buildChatDownloadUrl(attachment.mnemosyne_doc_id, attachment.file_name);
                        const loading = !!openingFileUrl && openingFileUrl === fileUrl;
                        // A pending entry has no stored row: nothing to fetch, nothing to download.
                        const hasRow = sentAttachmentRender(attachment) === SENT_ATTACHMENT_RENDER.CARD;
                        const openFile =
                            hasRow && onOpenFile && !loading
                                ? () =>
                                      onOpenFile({
                                          url: fileUrl,
                                          name: attachment.file_name,
                                          type: attachment.file_name.split('.').pop()?.toLowerCase(),
                                      })
                                : undefined;
                        return (
                            <div
                                key={attachment.mnemosyne_doc_id}
                                className={clsx(styles.Card, openFile && styles.Clickable, (!hasRow || loading) && styles.Busy)}
                                data-testid="sent-attachment-card"
                                aria-busy={loading}
                                {...(openFile && {
                                    role: 'button',
                                    tabIndex: 0,
                                    onClick: openFile,
                                    onKeyDown: (event: React.KeyboardEvent<HTMLDivElement>) => {
                                        // Space has no KEYBOARD_EVENTS constant; kept literal so the card
                                        // activates on both keys, per the a11y convention FileAttachmentCard follows.
                                        if (event.key !== KEYBOARD_EVENTS.ENTER && event.key !== ' ') return;
                                        event.preventDefault();
                                        openFile();
                                    },
                                })}
                            >
                                {loading ? (
                                    <span className={styles.FileIcon}>
                                        <LoadingSpinner height="1rem" color="var(--primary-purple)" />
                                    </span>
                                ) : (
                                    <img className={styles.FileIcon} src={getFileIcon(attachment.file_name)} alt="" />
                                )}
                                <span className={styles.FileName} title={attachment.file_name}>
                                    {attachment.file_name}
                                </span>
                                {hasRow && (
                                    <div className={styles.Kebab} onClick={(event) => event.stopPropagation()}>
                                        <FileActionsMenu
                                            items={[
                                                ...(openFile ? [{ name: CHAT_ATTACHMENT_COPY.OPEN, onClick: openFile }] : []),
                                                {
                                                    name: CHAT_ATTACHMENT_COPY.DOWNLOAD,
                                                    onClick: () => onDownload(fileUrl, attachment.file_name),
                                                },
                                            ]}
                                        />
                                    </div>
                                )}
                            </div>
                        );
                    })}
                    {hiddenCount > 0 && (
                        <Button
                            type="button"
                            className={styles.MoreLink}
                            onClick={() => setExpanded(true)}
                            data-testid="sent-attachment-more"
                            text={`+${hiddenCount} more`}
                        />
                    )}
                </div>
            )}
            {chipEntries.length > 0 && <AttachmentChips items={chipEntries.map(toSentChip)} />}
        </>
    );
};

export default SentAttachmentCards;
